import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import {
  Building2,
  CircleX,
  ExternalLink,
  Filter,
  Info,
  Landmark,
  Layers,
  MapPin,
  Phone,
  Search,
  UserRound,
} from "lucide-react";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import { nationalBankData } from "./NationalBank";
import { stateBankData } from "./StateBanks";
import { outOfStateBankData } from "./OutOfState";
import { stateIndustrialBankData } from "./StateIndustrial";
import { federalCreditUnionData } from "./FedralCredit";
import { federalSavingsAssociationData } from "./FedralSavings";
import { stateCreditUnionData } from "./StateCredit";
import { trustCompanyData } from "./TrustCompany";
import citiesData from "./cities.json";
import utahCounties from "../assets/utah-counties.json";
import "../styles/CharteredSchools.css";
import "../styles/finance.css";

const BRAND = {
  darkBlue: "#203feb",
  red: "#D62828",
  dark: "#1e293b",
  blue: "#3b82f6",
};

const CATEGORY_CONFIG = [
  { label: "National Banks", data: nationalBankData },
  { label: "State Banks", data: stateBankData },
  { label: "Out-of-State State Banks", data: outOfStateBankData },
  { label: "State Industrial Banks", data: stateIndustrialBankData },
  { label: "Federal Credit Unions", data: federalCreditUnionData },
  { label: "Federal Saving Associations", data: federalSavingsAssociationData },
  { label: "State Credit Unions", data: stateCreditUnionData },
  { label: "Trust Company", data: trustCompanyData },
];

const fipsToCounty = {
  "49001": "Beaver",
  "49003": "Box Elder",
  "49005": "Cache",
  "49007": "Carbon",
  "49009": "Daggett",
  "49011": "Davis",
  "49013": "Duchesne",
  "49015": "Emery",
  "49017": "Garfield",
  "49019": "Grand",
  "49021": "Iron",
  "49023": "Juab",
  "49025": "Kane",
  "49027": "Millard",
  "49029": "Morgan",
  "49031": "Piute",
  "49033": "Rich",
  "49035": "Salt Lake",
  "49037": "San Juan",
  "49039": "Sanpete",
  "49041": "Sevier",
  "49043": "Summit",
  "49045": "Tooele",
  "49047": "Uintah",
  "49049": "Utah",
  "49051": "Wasatch",
  "49053": "Washington",
  "49055": "Wayne",
  "49057": "Weber",
};

const normalizeText = (value = "") =>
  value
    .toUpperCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ")
    .trim();

const extractState = (address = "") => {
  const match = address.toUpperCase().match(/,\s*([A-Z]{2})\s+\d{5}(?:-\d+)?\s*$/);
  return match ? match[1] : "";
};

const extractFallbackCity = (address = "") => {
  const match = address.toUpperCase().match(/([A-Z][A-Z\s'-]+),\s*[A-Z]{2}\s+\d{5}(?:-\d+)?\s*$/);
  return match ? match[1].trim() : "Unknown";
};

const Finance = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tooltip, setTooltip] = useState("");
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const cityDirectory = useMemo(() => {
    const entries = citiesData.map((city) => {
      const normalizedName = normalizeText(city.name);
      return {
        city: city.name,
        county: city.county,
        normalizedName,
        pattern: `${normalizedName}, UT`,
      };
    });

    return entries.sort((a, b) => b.normalizedName.length - a.normalizedName.length);
  }, []);

  const institutions = useMemo(
    () =>
      CATEGORY_CONFIG.flatMap((category) =>
        category.data.map((item, index) => {
          const address = item.Address || item["Address"] || "";
          const normalizedAddress = normalizeText(address);
          const state = extractState(address);
          const cityMatch = cityDirectory.find(({ pattern }) =>
            normalizedAddress.includes(pattern)
          );
          const city = cityMatch
            ? cityMatch.city
            : extractFallbackCity(address)
                .toLowerCase()
                .replace(/\b\w/g, (char) => char.toUpperCase());
          const county =
            state !== "UT"
              ? "Out of State"
              : cityMatch?.county?.split("/")?.[0] || "Unknown";

          return {
            id: `${category.label}-${index}`,
            category: category.label,
            name: item["Bank Name"],
            address,
            leader: item["CEO/President"],
            phone: item["Contact No:"],
            city,
            county,
            state: state || "UT",
            mapsUrl: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              address
            )}`,
          };
        })
      ),
    [cityDirectory]
  );

  const countyCounts = useMemo(() => {
    const counts = {};

    institutions.forEach((institution) => {
      if (institution.state === "UT" && institution.county !== "Unknown") {
        counts[institution.county] = (counts[institution.county] || 0) + 1;
      }
    });

    return counts;
  }, [institutions]);

  const maxInstitutions = useMemo(() => {
    const counts = Object.values(countyCounts);
    return counts.length ? Math.max(...counts) : 0;
  }, [countyCounts]);

  const colorScale = scaleLinear()
    .domain([0, maxInstitutions || 1])
    .range(["#f1f5f9", BRAND.blue]);

  const filteredInstitutions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return institutions
      .filter((institution) => {
        const matchesCategory =
          selectedCategory === "All" || institution.category === selectedCategory;
        const matchesCounty = selectedCounty
          ? institution.county === selectedCounty
          : true;
        const matchesQuery = query
          ? [
              institution.name,
              institution.category,
              institution.address,
              institution.city,
              institution.county,
              institution.leader,
              institution.phone,
            ]
              .filter(Boolean)
              .some((value) => value.toLowerCase().includes(query))
          : true;

        return matchesCategory && matchesCounty && matchesQuery;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [institutions, searchQuery, selectedCategory, selectedCounty]);

  const utahCityCount = useMemo(
    () =>
      new Set(
        institutions
          .filter((institution) => institution.state === "UT")
          .filter((institution) => institution.city !== "Unknown")
          .map((institution) => institution.city)
      ).size,
    [institutions]
  );

  const outOfStateCount = useMemo(
    () =>
      institutions.filter((institution) => institution.state !== "UT").length,
    [institutions]
  );

  const activeCountyLabel = selectedCounty
    ? `${selectedCounty} County`
    : "Search Results";

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-blue-50 to-white">
      <LandingSection
        data={{
          imageName: "finance3.webp",
          title: "Utah's Financial Institutions",
          visible: "",
        }}
      />

      <div className="charter-explorer-container">
        <div className="finance-intro card-glass">
          <div>
            <p className="finance-eyebrow">Finance explorer</p>
            <h2>Browse Utah financial institutions with a map-first view</h2>
          </div>
          <p>
            Search across banks, credit unions, trust companies, and savings
            institutions, then use the county map and category filters to focus
            on where each group is concentrated.
          </p>
        </div>

        <div className="finance-filter-bar card-glass">
          <div className="finance-filter-title">
            <Filter size={18} />
            <span>Institution Type</span>
          </div>
          <div className="finance-chip-row">
            <button
              type="button"
              className={`finance-chip ${selectedCategory === "All" ? "active" : ""}`}
              onClick={() => setSelectedCategory("All")}
            >
              All
            </button>
            {CATEGORY_CONFIG.map((category) => (
              <button
                key={category.label}
                type="button"
                className={`finance-chip ${
                  selectedCategory === category.label ? "active" : ""
                }`}
                onClick={() => setSelectedCategory(category.label)}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        <div className="explorer-grid">
          <div className="map-panel">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="card-glass map-container"
            >
              <div className="map-header">
                <h3>
                  <Layers size={20} /> Interactive County Map
                </h3>
                <p>Click a county to narrow the institution list instantly</p>
              </div>

              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ scale: 6000, center: [-111.9, 39.3] }}
                className="rsm-map"
              >
                <Geographies geography={utahCounties}>
                  {({ geographies }) =>
                    geographies
                      .filter((geo) => geo.id.startsWith("49"))
                      .map((geo) => {
                        const countyName = fipsToCounty[geo.id];
                        const institutionCount = countyCounts[countyName] || 0;

                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={
                              selectedCounty === countyName
                                ? BRAND.red
                                : colorScale(institutionCount)
                            }
                            stroke="#fff"
                            strokeWidth={1}
                            onMouseEnter={(event) => {
                              setTooltip(
                                `${countyName}: ${institutionCount} institutions`
                              );
                              setTooltipPos({
                                x: event.clientX,
                                y: event.clientY,
                              });
                            }}
                            onMouseMove={(event) => {
                              setTooltipPos({
                                x: event.clientX,
                                y: event.clientY,
                              });
                            }}
                            onMouseLeave={() => setTooltip("")}
                            onClick={() => setSelectedCounty(countyName)}
                            style={{
                              default: {
                                outline: "none",
                                transition: "all 0.3s",
                              },
                              hover: {
                                fill: BRAND.red,
                                outline: "none",
                                cursor: "pointer",
                              },
                              pressed: { outline: "none" },
                            }}
                          />
                        );
                      })
                  }
                </Geographies>
              </ComposableMap>

              <div className="map-legend">
                <span>0 Institutions</span>
                <div className="legend-gradient" />
                <span>{maxInstitutions} Institutions</span>
              </div>
            </motion.div>
          </div>

          <div className="data-panel">
            <div className="stats-row">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                className="stat-card"
              >
                <Landmark size={24} color={BRAND.darkBlue} />
                <div>
                  <span className="stat-val">{institutions.length}</span>
                  <span className="stat-lab">Total Institutions</span>
                </div>
              </motion.div>

              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="stat-card"
              >
                <Building2 size={24} color={BRAND.red} />
                <div>
                  <span className="stat-val">{CATEGORY_CONFIG.length}</span>
                  <span className="stat-lab">Institution Types</span>
                </div>
              </motion.div>
            </div>

            <div className="stats-row finance-stats-row">
              <div className="stat-card">
                <MapPin size={24} color={BRAND.blue} />
                <div>
                  <span className="stat-val">{utahCityCount}</span>
                  <span className="stat-lab">Utah Cities Represented</span>
                </div>
              </div>

              <div className="stat-card">
                <Info size={24} color={BRAND.red} />
                <div>
                  <span className="stat-val">{outOfStateCount}</span>
                  <span className="stat-lab">Out-of-State Offices</span>
                </div>
              </div>
            </div>

            <div className="search-box card-glass">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search by institution, city, county, leader, phone..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              {(searchQuery || selectedCounty || selectedCategory !== "All") && (
                <button
                  className="clear-search-btn"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCounty(null);
                    setSelectedCategory("All");
                  }}
                  title="Clear all filters"
                >
                  <CircleX size={18} />
                </button>
              )}
            </div>

            <div className="results-container">
              <AnimatePresence mode="wait">
                {selectedCounty || searchQuery || selectedCategory !== "All" ? (
                  <motion.div
                    key={`${selectedCounty}-${selectedCategory}-${searchQuery}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="county-results"
                  >
                    <div className="county-header">
                      <h2>
                        {selectedCounty
                          ? activeCountyLabel
                          : selectedCategory === "All"
                          ? "Search Results"
                          : selectedCategory}
                      </h2>
                      <div className="header-actions">
                        <span className="count-badge">
                          {filteredInstitutions.length} Result
                          {filteredInstitutions.length !== 1 ? "s" : ""}
                        </span>
                        <button
                          className="clear-all-small"
                          onClick={() => {
                            setSearchQuery("");
                            setSelectedCounty(null);
                            setSelectedCategory("All");
                          }}
                        >
                          Clear All
                        </button>
                      </div>
                    </div>

                    {filteredInstitutions.length > 0 ? (
                      <div className="school-list">
                        {filteredInstitutions.map((institution, index) => (
                          <motion.div
                            key={institution.id}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.02 }}
                            className="school-card card-glass"
                          >
                            <div className="finance-card-header">
                              <div>
                                <h4>{institution.name}</h4>
                                <p className="finance-card-subtitle">
                                  {institution.category}
                                </p>
                              </div>
                              <span className="finance-card-badge">
                                {institution.county}
                              </span>
                            </div>

                            <div className="finance-meta-grid">
                              <span>
                                <MapPin size={14} /> {institution.city},{" "}
                                {institution.state}
                              </span>
                              <span>
                                <Phone size={14} /> {institution.phone}
                              </span>
                              <span>
                                <UserRound size={14} /> {institution.leader}
                              </span>
                              <a
                                href={institution.mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="finance-map-link"
                              >
                                View address <ExternalLink size={14} />
                              </a>
                            </div>

                            <p className="finance-address">{institution.address}</p>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-data">
                        <Info size={40} />
                        <p>
                          No institutions found for the current filters or search.
                        </p>
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="welcome-box card-glass"
                  >
                    <Landmark size={60} />
                    <h2>Utah Financial Institutions Explorer</h2>
                    <p>
                      Start with a category, county, or search term to explore
                      Utah’s finance ecosystem beyond a simple table.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <div
        className="charter-explorer-container"
        style={{ paddingBottom: "2rem" }}
      >
        <div className="card-glass" style={{ padding: "2rem", marginTop: "2rem" }}>
          <div className="flex items-center gap-2 mb-4">
            <Info size={24} color={BRAND.darkBlue} />
            <h3 className="text-xl font-bold" style={{ color: BRAND.dark }}>
              Data Sources
            </h3>
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-sm opacity-70 mb-2">
              For more detailed information, please visit the following resources:
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                target="_blank"
                href="https://www.utah.gov/dfi/FinancialInstitutions.html"
                rel="noreferrer"
                className="school-link flex items-center gap-1"
              >
                Utah Government - Financial Institutions{" "}
                <ExternalLink size={14} />
              </a>
              <a
                target="_blank"
                href="https://business.utah.gov/targeted-industries/finance/"
                rel="noreferrer"
                className="school-link flex items-center gap-1"
              >
                Utah Business - Finance Industry <ExternalLink size={14} />
              </a>
              <a
                target="_blank"
                href="https://wallethub.com/insurance-companies#loc=Utah"
                rel="noreferrer"
                className="school-link flex items-center gap-1"
              >
                WalletHub - Insurance Companies <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {tooltip && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            style={{
              position: "fixed",
              top: tooltipPos.y - 50,
              left: tooltipPos.x + 20,
              pointerEvents: "none",
              zIndex: 9999,
              background: "rgba(15, 23, 42, 0.9)",
              backdropFilter: "blur(8px)",
              color: "white",
              padding: "8px 12px",
              borderRadius: "8px",
              fontSize: "0.85rem",
              fontWeight: "600",
              boxShadow:
                "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              whiteSpace: "nowrap",
            }}
          >
            {tooltip}
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default Finance;
